import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Modal } from '@/shared/ui/Modal';
import type { CommunityOption, CommunityState, PartnerRow, Pick, PlanTouchInput } from '../api/communityApi';
import styles from './Community.module.css';

export type PlanTarget =
  | { kind: 'pick'; pick: Pick }
  | { kind: 'partner'; partner: PartnerRow }
  | { kind: 'new' };

function defaultActivity(target: PlanTarget): string {
  if (target.kind === 'pick') return target.pick.kind === 'event' ? 'attend_event' : target.pick.suggestedTouch;
  if (target.kind === 'partner') return 'handwritten_note';
  return 'coffee';
}

/** A date input value (YYYY-MM-DD) for the viewer's local calendar day, never the UTC day. */
function localDay(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function inSevenDays(): string {
  return localDay(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
}

/**
 * Plan a touch: activity chips, date, the reconnect rhythm, private notes, and (when Joe is
 * active and the activity needs words) the option to have Joe draft it. Also "Add someone I know"
 * in add mode. Saving creates the planned touch; nothing is sent.
 */
export function PlanTouchModal({
  target,
  vocabulary,
  joeActive,
  categories,
  busy,
  onClose,
  onSave,
}: {
  target: PlanTarget | null;
  vocabulary: CommunityState['vocabulary'];
  joeActive: boolean;
  categories: CommunityOption[];
  busy: boolean;
  onClose: () => void;
  onSave: (input: PlanTouchInput) => void;
}) {
  const open = target !== null;
  const [activity, setActivity] = useState('coffee');
  const [activityOther, setActivityOther] = useState('');
  const [date, setDate] = useState(inSevenDays());
  const [rhythm, setRhythm] = useState('6m');
  const [rhythmDays, setRhythmDays] = useState('90');
  const [notes, setNotes] = useState('');
  const [draft, setDraft] = useState(true);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [org, setOrg] = useState('');
  const [category, setCategory] = useState(categories[0]?.key ?? 'general_dentist');

  useEffect(() => {
    if (!target) return;
    setActivity(defaultActivity(target));
    setActivityOther('');
    setDate(target.kind === 'pick' && target.pick.eventStart ? localDay(new Date(target.pick.eventStart)) : inSevenDays());
    setRhythm(target.kind === 'partner' ? target.partner.rhythm : target.kind === 'pick' && target.pick.kind === 'event' ? 'once' : '6m');
    setRhythmDays(target.kind === 'partner' && target.partner.rhythmDays ? String(target.partner.rhythmDays) : '90');
    setNotes('');
    setDraft(true);
    setName('');
    setRole('');
    setOrg('');
    setCategory(categories[0]?.key ?? 'general_dentist');
  }, [target, categories]);

  const needsWords = useMemo(() => vocabulary.touchTypes.find((t) => t.key === activity)?.needsWords ?? false, [vocabulary, activity]);
  const title = target?.kind === 'pick' ? target.pick.name : target?.kind === 'partner' ? target.partner.name : 'Add someone I know';
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(date) && !Number.isNaN(new Date(`${date}T12:00:00`).getTime());
  const canSave = dateValid && (target?.kind !== 'new' || name.trim().length > 0);

  const save = () => {
    if (!target) return;
    const base: PlanTouchInput = {
      activityType: activity,
      activityOther: activity === 'other' ? activityOther : null,
      date: new Date(`${date}T12:00:00`).toISOString(),
      rhythm,
      rhythmDays: rhythm === 'custom' ? Number(rhythmDays) || null : null,
      notes: notes.trim() || null,
      draftRequested: joeActive && needsWords && draft,
    };
    if (target.kind === 'pick') onSave({ ...base, pickId: target.pick.id });
    else if (target.kind === 'partner') onSave({ ...base, partnerId: target.partner.id });
    else onSave({ ...base, partner: { name: name.trim(), role: role.trim() || null, org: org.trim() || null, category } });
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={target?.kind === 'new' ? 'Add someone I know' : 'Plan a touch'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={busy || !canSave} loading={busy}>
            Save to my network
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {target?.kind === 'new' ? (
          <>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Jane Example" fullWidth />
            <div className={styles.twoCol}>
              <Input label="Role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Pediatrician" fullWidth />
              <Input label="Organisation" value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Example Pediatrics" fullWidth />
            </div>
            <div>
              <div className={styles.fieldLabel}>Category</div>
              <div className={styles.chips}>
                {categories.map((c) => (
                  <button key={c.key} type="button" className={clsx(styles.chip, category === c.key && styles.chipActive)} onClick={() => setCategory(c.key)}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className={styles.modalName}>{title}</div>
        )}
        <div>
          <div className={styles.fieldLabel}>How do you want to connect?</div>
          <div className={styles.chips}>
            {vocabulary.touchTypes.map((t) => (
              <button key={t.key} type="button" className={clsx(styles.chip, activity === t.key && styles.chipActive)} onClick={() => setActivity(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
          {activity === 'other' && (
            <Input value={activityOther} onChange={(e) => setActivityOther(e.target.value)} placeholder="What is the touch?" fullWidth />
          )}
        </div>
        <div className={styles.twoCol}>
          <Input type="date" label="When" value={date} onChange={(e) => setDate(e.target.value)} fullWidth />
          {rhythm === 'custom' && (
            <Input type="number" label="Every N days" min={7} max={730} value={rhythmDays} onChange={(e) => setRhythmDays(e.target.value)} fullWidth />
          )}
        </div>
        <div>
          <div className={styles.fieldLabel}>Remind me to reconnect</div>
          <div className={styles.chips}>
            {vocabulary.rhythms.map((r) => (
              <button key={r.key} type="button" className={clsx(styles.chip, rhythm === r.key && styles.chipActive)} onClick={() => setRhythm(r.key)}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={styles.fieldLabel} htmlFor="community-touch-notes">
            Notes and follow-ups (only you see this)
          </label>
          <textarea id="community-touch-notes" className={styles.textarea} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </div>
        {joeActive && needsWords && (
          <label className={styles.checkboxRow}>
            <input type="checkbox" checked={draft} onChange={(e) => setDraft(e.target.checked)} />
            Have Joe draft the note or invite for my review
          </label>
        )}
      </div>
    </Modal>
  );
}
